import { useCallback, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { base64ToFileConverter } from 'components/misc/functions';
import { HugeiconsIcon } from '@hugeicons/react';
import { Camera01Icon, RefreshIcon } from '@hugeicons/core-free-icons';

function Camera({ name, setPreview, setValue, closeModal }) {
    const cameraRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(null);
    const [videoConstraints, setVideoConstraints] = useState({ facingMode: 'user' });
    const [isMobile, setIsMobile] = useState(false);

    const isSelfie = name === 'selfie-photo';

    useEffect(() => {
        const mobile = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        setIsMobile(mobile);
        setVideoConstraints({
            facingMode: mobile
                ? { exact: isSelfie ? 'user' : 'environment' }
                : 'user',
        });
    }, [name, isSelfie]);

    const captureImage = useCallback(() => {
        const img = cameraRef.current.getScreenshot({ width: 600, height: 600 });
        setImgSrc(img);
        setPreview(img);
        setValue(base64ToFileConverter(img, `${name}.jpeg`));
    }, [cameraRef, name, setPreview, setValue]);

    const retakeImage = () => {
        setImgSrc(null);
        setValue('');
    };

    const confirmAndClose = () => {
        if (closeModal) closeModal();
    };

    return (
        <div className="flex flex-col">
            {/* Camera / preview area */}
            <div className="relative bg-black overflow-hidden" style={{ minHeight: 320 }}>
                {imgSrc ? (
                    <img
                        src={imgSrc}
                        alt="Captured"
                        className="w-full h-auto object-cover"
                    />
                ) : (
                    <>
                        {isMobile ? (
                            <div className="relative flex justify-center items-center" style={{ height: 420 }}>
                                <Webcam
                                    ref={cameraRef}
                                    mirrored={isSelfie}
                                    videoConstraints={videoConstraints}
                                    screenshotFormat="image/jpeg"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                                {isSelfie && (
                                    <div className="relative z-10 w-64 h-80 border-4 border-white/80 rounded-[50%/60%_60%_40%_40%] rotate-180 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
                                )}
                            </div>
                        ) : (
                            <Webcam
                                height={380}
                                width="100%"
                                ref={cameraRef}
                                mirrored={false}
                                videoConstraints={videoConstraints}
                                screenshotFormat="image/jpeg"
                                className="w-full"
                            />
                        )}
                    </>
                )}
            </div>

            {/* Action bar */}
            <div className="flex items-center justify-between gap-3 px-5 py-4 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-white/[.08]">
                <div className="flex items-center gap-2">
                    {!imgSrc ? (
                        <button
                            type="button"
                            onClick={captureImage}
                            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 hover:bg-amber-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors"
                        >
                            <HugeiconsIcon icon={Camera01Icon} size={16} />
                            Capture photo
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={retakeImage}
                                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            >
                                <HugeiconsIcon icon={RefreshIcon} size={15} />
                                Retake
                            </button>
                            <button
                                type="button"
                                onClick={confirmAndClose}
                                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors"
                            >
                                Use photo
                            </button>
                        </>
                    )}
                </div>

                <button
                    type="button"
                    onClick={confirmAndClose}
                    className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                    Close
                </button>
            </div>
        </div>
    );
}

export default Camera;
